import { useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";

// H4 — Live "1,200+ sessions delivered" counter on the hero.
// Animated count-up when the strip enters the viewport. Number is sourced
// from SESSIONS_DELIVERED in constants.js — bump it as the clinic grows.

export default function TrustCounter({ target, label, suffix = "+" }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const playedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            // Count up over ~1.6s with eased ramp.
            const duration = 1600;
            const start = performance.now();
            const step = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <section
      ref={ref}
      data-testid="trust-counter"
      className="bg-[#080808] text-white border-y border-[#1a1a1a]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-center sm:text-left">
        <Activity size={28} className="text-[#9DD4D8] shrink-0" strokeWidth={1.5} />
        <div className="flex items-baseline gap-3 flex-wrap justify-center">
          <span
            data-testid="trust-counter-value"
            className="text-5xl md:text-6xl lg:text-7xl font-bold tabular-nums tracking-tight text-white"
          >
            {value.toLocaleString()}
            <span className="text-[#9DD4D8]">{suffix}</span>
          </span>
          <span className="text-base md:text-lg text-[#cbd5e1] font-semibold uppercase tracking-[0.18em]">
            {label}
          </span>
        </div>
      </div>
    </section>
  );
}
